import React from 'react';
import {
  Box,
  Button,
  Flex,
  IconButton,
  Select,
  Switch,
  Text,
  TextField,
} from '@radix-ui/themes';
import { observer } from 'mobx-react-lite';

import { FormField } from 'shared/components';
import { AutocompleteV2 } from 'shared/components/autocomplete-v2';
import { AddIcon, ClearIcon, FileUploadIcon, SearchIcon } from 'shared/icons';

import { useCreateResumeContext } from '../create-resume.context';
import { getRequiredFieldError } from '../create-resume.validation';
import {
  AdditionalEducationItem,
  EducationInfoStore,
  EducationItem,
} from './education-info.store';

type EducationInfoProps = {
  renderSteps: (valid: boolean) => React.ReactNode;
  showErrors: boolean;
};

type DateFields =
  | 'fromMonth'
  | 'fromYear'
  | 'toMonth'
  | 'toYear'
  | 'stillLearning';

const EDUCATION_DATE_RANGE_ERROR =
  'Дата початку навчання не може бути пізніше дати закінчення';

const getEducationDateYearRangeError = (
  stillLearning: boolean,
  fromYearValue: string,
  toYearValue: string
): string | null => {
  const fromYear = Number(fromYearValue);
  const toYear = Number(toYearValue);

  if (
    stillLearning ||
    !fromYearValue.trim() ||
    !toYearValue.trim() ||
    Number.isNaN(fromYear) ||
    Number.isNaN(toYear)
  ) {
    return null;
  }

  return fromYear > toYear ? EDUCATION_DATE_RANGE_ERROR : null;
};

const getEducationDateMonthRangeError = ({
  stillLearning,
  fromYearValue,
  toYearValue,
  fromMonthValue,
  toMonthValue,
}: {
  stillLearning: boolean;
  fromYearValue: string;
  toYearValue: string;
  fromMonthValue: string;
  toMonthValue: string;
}): string | null => {
  const fromYear = Number(fromYearValue);
  const toYear = Number(toYearValue);
  const fromMonth = Number(fromMonthValue);
  const toMonth = Number(toMonthValue);

  if (
    stillLearning ||
    !fromYearValue.trim() ||
    !toYearValue.trim() ||
    !fromMonthValue.trim() ||
    !toMonthValue.trim() ||
    Number.isNaN(fromYear) ||
    Number.isNaN(toYear) ||
    Number.isNaN(fromMonth) ||
    Number.isNaN(toMonth) ||
    fromYear !== toYear
  ) {
    return null;
  }

  return fromMonth > toMonth ? EDUCATION_DATE_RANGE_ERROR : null;
};

async function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      resolve(String(reader.result ?? ''));
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

const EducationDateRange = observer(
  ({
    months,
    years,
    fromMonth,
    fromYear,
    toMonth,
    toYear,
    stillLearning,
    requiredStart = false,
    requiredEnd = false,
    showErrors,
    onChange,
  }: {
    months: EducationInfoStore['months'];
    years: EducationInfoStore['years'];
    fromMonth: string;
    fromYear: string;
    toMonth: string;
    toYear: string;
    stillLearning: boolean;
    requiredStart?: boolean;
    requiredEnd?: boolean;
    showErrors: boolean;
    onChange: (field: DateFields, value: string | boolean) => void;
  }) => (
    <Flex direction="column" gap="3">
      <Flex gap="4">
        <Flex direction="column" gap="2" width="100%">
          <Text size="2">Початок навчання</Text>
          <Flex gap="3">
            <FormField
              label="Рік"
              required={requiredStart}
              error={getRequiredFieldError(
                showErrors,
                !requiredStart || Boolean(fromYear.trim())
              ) ?? getEducationDateYearRangeError(stillLearning, fromYear, toYear)}
            >
              <Select.Root
                size="3"
                value={fromYear}
                onValueChange={(value) => {
                  onChange('fromYear', value);
                }}
              >
                <Select.Trigger color="indigo" placeholder="Оберіть рік" />
                <Select.Content color="indigo" position="popper">
                  {years.map((year) => (
                    <Select.Item key={year} value={String(year)}>
                      {year}
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select.Root>
            </FormField>

            <FormField
              label="Місяць"
              required={requiredStart}
              error={getRequiredFieldError(
                showErrors,
                !requiredStart || Boolean(fromMonth.trim())
              ) ??
                getEducationDateMonthRangeError({
                  stillLearning,
                  fromYearValue: fromYear,
                  toYearValue: toYear,
                  fromMonthValue: fromMonth,
                  toMonthValue: toMonth,
                })}
            >
              <Select.Root
                size="3"
                value={fromMonth}
                onValueChange={(value) => {
                  onChange('fromMonth', value);
                }}
              >
                <Select.Trigger color="indigo" placeholder="Оберіть місяць" />
                <Select.Content color="indigo" position="popper">
                  {months.map((month) => (
                    <Select.Item key={month.id} value={String(month.id)}>
                      {month.name}
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select.Root>
            </FormField>
          </Flex>
        </Flex>

        <Flex direction="column" gap="2" width="100%">
          <Text size="2">Кінець навчання</Text>
          <Flex gap="3">
            <FormField
              label="Рік"
              required={requiredEnd && !stillLearning}
              error={getRequiredFieldError(
                showErrors,
                stillLearning || !requiredEnd || Boolean(toYear.trim())
              )}
            >
              <Select.Root
                size="3"
                disabled={stillLearning}
                value={toYear}
                onValueChange={(value) => {
                  onChange('toYear', value);
                }}
              >
                <Select.Trigger color="indigo" placeholder="Оберіть рік" />
                <Select.Content color="indigo" position="popper">
                  {years.map((year) => (
                    <Select.Item key={year} value={String(year)}>
                      {year}
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select.Root>
            </FormField>

            <FormField
              label="Місяць"
              required={requiredEnd && !stillLearning}
              error={getRequiredFieldError(
                showErrors,
                stillLearning || !requiredEnd || Boolean(toMonth.trim())
              )}
            >
              <Select.Root
                size="3"
                disabled={stillLearning}
                value={toMonth}
                onValueChange={(value) => {
                  onChange('toMonth', value);
                }}
              >
                <Select.Trigger color="indigo" placeholder="Оберіть місяць" />
                <Select.Content color="indigo" position="popper">
                  {months.map((month) => (
                    <Select.Item key={month.id} value={String(month.id)}>
                      {month.name}
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select.Root>
            </FormField>
          </Flex>
        </Flex>
      </Flex>

      <Flex align="center" gap="2">
        <Switch
          checked={stillLearning}
          onCheckedChange={(checked) => {
            onChange('stillLearning', checked);
          }}
        />
        <Text size="2">Ще навчаюсь</Text>
      </Flex>
    </Flex>
  )
);

const MainEducationCard = observer(
  ({
    education,
    educationInfo,
    showErrors,
  }: {
    education: EducationItem;
    educationInfo: EducationInfoStore;
    showErrors: boolean;
  }) => (
    <Box p="4" style={{ border: '1px solid var(--gray-a6)', borderRadius: 8 }}>
      <Flex direction="column" gap="4">
        <Flex justify="between" align="center">
          <Text weight="medium">Освіта</Text>
          <IconButton
            type="button"
            variant="soft"
            color="red"
            disabled={educationInfo.educations.length === 1}
            onClick={() => {
              educationInfo.removeEducation(education.id);
            }}
          >
            <ClearIcon />
          </IconButton>
        </Flex>

        <FormField
          label="Рівень освіти"
          required
          error={getRequiredFieldError(showErrors, Boolean(education.eduType.trim()))}
        >
          <Select.Root
            size="3"
            value={education.eduType}
            onValueChange={(value) => {
              educationInfo.updateField(education.id, 'eduType', value);
            }}
          >
            <Select.Trigger color="indigo" placeholder="Оберіть рівень освіти" />
            <Select.Content color="indigo" position="popper">
              {educationInfo.educationTypes.map((type) => (
                <Select.Item key={type.value} value={String(type.value)}>
                  {type.name}
                </Select.Item>
              ))}
            </Select.Content>
          </Select.Root>
        </FormField>

        <FormField
          label="Навчальний заклад"
          required
          error={getRequiredFieldError(
            showErrors,
            Boolean(education.institution.trim())
          )}
        >
          <AutocompleteV2.Root
            items={education.institutionAutocompleteOptions}
            viewModel={education.institutionVM}
            getOptionValue={(institution) => institution.id}
            getOptionLabel={(institution) => institution.name}
            color="indigo"
            indicator={<SearchIcon width={20} height={20} />}
            rotateIndicatorOnOpen={false}
            loading={education.isInstitutionLoading}
            placeholder="Введіть назву навчального закладу"
            resetInputOnClose="never"
            onSearch={education.onSearchInstitution}
            onSelect={education.onSelectInstitution}
          />
        </FormField>

        {education.showDetails ? (
          <>
            <FormField label="Факультет">
              <AutocompleteV2.Root
                items={education.facultyAutocompleteOptions}
                viewModel={education.facultyVM}
                getOptionValue={(faculty) => faculty.id}
                getOptionLabel={(faculty) => faculty.name}
                color="indigo"
                indicator={<SearchIcon width={20} height={20} />}
                rotateIndicatorOnOpen={false}
                loading={education.isFacultiesLoading}
                placeholder={
                  education.institution
                    ? 'Оберіть або введіть факультет'
                    : 'Спочатку оберіть навчальний заклад'
                }
                resetInputOnClose="never"
                onSearch={education.onSearchFaculty}
                onSelect={education.onSelectFaculty}
              />
            </FormField>

            <FormField label="Спеціальність">
              <AutocompleteV2.Root
                items={education.specialtyAutocompleteOptions}
                viewModel={education.specialtyVM}
                getOptionValue={(specialty) => specialty.id}
                getOptionLabel={(specialty) => specialty.name}
                color="indigo"
                indicator={<SearchIcon width={20} height={20} />}
                rotateIndicatorOnOpen={false}
                loading={education.isSpecialtyLoading}
                placeholder="Введіть назву спеціальності"
                resetInputOnClose="never"
                onSearch={education.onSearchSpecialty}
                onSelect={education.onSelectSpecialty}
              />
            </FormField>
          </>
        ) : null}

        <EducationDateRange
          months={educationInfo.months}
          years={educationInfo.years}
          fromMonth={education.fromMonth}
          fromYear={education.fromYear}
          toMonth={education.toMonth}
          toYear={education.toYear}
          stillLearning={education.stillLearning}
          requiredStart
          requiredEnd
          showErrors={showErrors}
          onChange={(field, value) => {
            educationInfo.updateField(
              education.id,
              field,
              value as never
            );
          }}
        />
      </Flex>
    </Box>
  )
);

const AdditionalEducationCard = observer(
  ({
    education,
    educationInfo,
    showErrors,
  }: {
    education: AdditionalEducationItem;
    educationInfo: EducationInfoStore;
    showErrors: boolean;
  }) => (
    <Box p="4" style={{ border: '1px solid var(--gray-a6)', borderRadius: 8 }}>
      <Flex direction="column" gap="4">
        <Flex justify="between" align="center">
          <Text weight="medium">Додаткова освіта / курси</Text>
          <IconButton
            type="button"
            variant="soft"
            color="red"
            disabled={educationInfo.additionalEducations.length === 1}
            onClick={() => {
              educationInfo.removeAdditionalEducation(education.id);
            }}
          >
            <ClearIcon />
          </IconButton>
        </Flex>

        <FormField label="Назва освіти / курсу">
          <TextField.Root
            size="3"
            color="indigo"
            value={education.courseName}
            placeholder="Введіть назву освіти або курсу"
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              educationInfo.updateAdditionalField(
                education.id,
                'courseName',
                event.target.value
              );
            }}
          />
        </FormField>

        <FormField label="Навчальний заклад">
          <TextField.Root
            size="3"
            color="indigo"
            value={education.institution}
            placeholder="Введіть навчальний заклад"
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              educationInfo.updateAdditionalField(
                education.id,
                'institution',
                event.target.value
              );
            }}
          />
        </FormField>

        <FormField label="Назва документа">
          <TextField.Root
            size="3"
            color="indigo"
            value={education.documentName}
            placeholder="Введіть назву документа"
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              educationInfo.updateAdditionalField(
                education.id,
                'documentName',
                event.target.value
              );
            }}
          />
        </FormField>

        <FormField label="Посилання на сертифікат">
          <TextField.Root
            size="3"
            color="indigo"
            value={education.courseLink}
            placeholder="Додайте посилання"
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              educationInfo.updateAdditionalField(
                education.id,
                'courseLink',
                event.target.value
              );
            }}
          />
        </FormField>

        <EducationDateRange
          months={educationInfo.months}
          years={educationInfo.years}
          fromMonth={education.fromMonth}
          fromYear={education.fromYear}
          toMonth={education.toMonth}
          toYear={education.toYear}
          stillLearning={education.stillLearning}
          requiredStart={education.hasDateValues}
          requiredEnd={education.hasDateValues}
          showErrors={showErrors}
          onChange={(field, value) => {
            educationInfo.updateAdditionalField(
              education.id,
              field,
              value as never
            );
          }}
        />

        <Box mt="2">
          <FormField label="Сертифікат">
            <Flex direction="column" gap="2" align="start">
            <Flex align="center" gap="3">
              <Box
                width="300px"
                height="164px"
                style={{
                  border: '1px solid var(--gray-a6)',
                  borderRadius: 8,
                  boxSizing: 'border-box',
                  overflow: 'hidden',
                  padding: 4,
                }}
              >
                {education.certificate ? (
                  <img
                    alt="Сертифікат"
                    src={education.certificate}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                ) : (
                  <Box
                    asChild
                    width="100%"
                    height="100%"
                    style={{ cursor: 'pointer' }}
                    onDragOver={(event) => {
                      event.preventDefault();
                    }}
                    onDrop={async (event) => {
                      event.preventDefault();
                      const file = event.dataTransfer.files?.[0];
                      if (!file) {
                        return;
                      }

                      educationInfo.updateAdditionalField(
                        education.id,
                        'certificate',
                        await fileToBase64(file)
                      );
                    }}
                  >
                    <label htmlFor={`certificate-${education.id}`}>
                      <Flex
                        align="center"
                        justify="center"
                        direction="column"
                        gap="2"
                        width="100%"
                        height="100%"
                        p="3"
                      >
                        <Text color="blue" highContrast>
                          <FileUploadIcon width={24} height={24} />
                        </Text>
                        <Text size="2" weight="bold" align="center">
                          Перетягніть файл
                          <br />
                          або{' '}
                          <Text as="span" color="blue" weight="bold">
                            завантажте
                          </Text>
                        </Text>
                      </Flex>
                    </label>
                  </Box>
                )}
              </Box>
              <input
                id={`certificate-${education.id}`}
                type="file"
                accept=".jpeg,.jpg,.png"
                hidden
                onChange={async (event) => {
                  const file = event.target.files?.[0];
                  if (!file) {
                    return;
                  }

                  educationInfo.updateAdditionalField(
                    education.id,
                    'certificate',
                    await fileToBase64(file)
                  );
                  event.target.value = '';
                }}
              />
              <Button
                type="button"
                variant="soft"
                color="red"
                disabled={!education.certificate}
                onClick={() => {
                  educationInfo.updateAdditionalField(education.id, 'certificate', '');
                }}
              >
                Видалити
              </Button>
            </Flex>
            <Text size="1" color="gray" align="center" style={{ width: 300 }}>
              Підтримуються файли jpeg, png, jpg до 5 МБ
            </Text>
            </Flex>
          </FormField>
        </Box>
      </Flex>
    </Box>
  )
);

const EducationInfoView = ({ renderSteps, showErrors }: EducationInfoProps) => {
  const { educationInfo } = useCreateResumeContext();

  return (
    <form>
      <Box>
        <Flex direction="column" gap="5" my="4">
          {educationInfo.educations.map((education) => (
            <MainEducationCard
              key={education.id}
              education={education}
              educationInfo={educationInfo}
              showErrors={showErrors}
            />
          ))}

          <Button
            type="button"
            variant="outline"
            onClick={() => {
              educationInfo.addEducation();
            }}
          >
            Додати освіту <AddIcon />
          </Button>

          <Text as="div" size="4" weight="medium">
            Додаткова освіта / курси
          </Text>

          {educationInfo.additionalEducations.map((education) => (
            <AdditionalEducationCard
              key={education.id}
              education={education}
              educationInfo={educationInfo}
              showErrors={showErrors}
            />
          ))}

          <Button
            type="button"
            variant="outline"
            onClick={() => {
              educationInfo.addAdditionalEducation();
            }}
          >
            Додати курс <AddIcon />
          </Button>
        </Flex>
      </Box>

      <Flex mt="4">{renderSteps(educationInfo.valid)}</Flex>
    </form>
  );
};

export const EducationInfo = observer(EducationInfoView);
